// CreatePurchase.tsx
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Dropdown, Spinner } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import { FaInfoCircle } from "react-icons/fa";
import { HiOutlineCube, HiOutlineBolt, HiOutlineTrash, HiOutlinePlus, HiOutlineArchiveBox, HiOutlineArrowRight, HiOutlineQueueList } from "react-icons/hi2";
import { createIngredientPurchaseRecord, createBatchIngredientPurchaseRecords, CreatePurchasePayload } from "../../../../../../../../crud/management_api/ingredient_purchases";
import { getAllIngredientsByLocalId, getIngredientById } from "../../../../../../../../crud/menu_api/ingredients";
import { getAllProvidersByLocalId } from "../../../../../../../../crud/management_api/providers";
import { getAllBrandsByLocalId } from "../../../../../../../../crud/menu_api/brands";
import { getAllUnidadesMedida } from "../../../../../../../../crud/configuration_api/unidades_medida";
import { TaxType, TAX_TYPE_OPTIONS, ExpenseCategoryType } from "../../../../types";
import { toast } from "react-toastify";
import "./CreatePurchase.scss";

interface Ingredient {
  ingredient_id: number;
  ingredient_name: string;
}

interface Provider {
  provider_id: number;
  name: string;
}

interface Unit {
  unidad_medida_id: number;
  name: string;
}

interface Brand {
  brand_id: number;
  brand_name: string;
}

interface StorageUnit {
  name: string;
  is_global: boolean;
}

interface IngredientStock {
  storage_unit_id: number;
  unidad_medida_id: number;
  system_stock: number;
  reported_stock: number;
  min_quantity: number;
  sensitivity: number;
  is_following: boolean;
  is_favorite: boolean;
  last_stock_update: string;
  storage_unit: StorageUnit;
}

interface CreatePurchaseProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onCreated: () => void;
  categories: ExpenseCategoryType[];
}

type PurchaseMode = 'existing' | 'quick';

interface PendingItem {
  _key: number;
  mode: 'existing' | 'quick';
  displayName: string;
  ingredientId?: number;
  ingredientName?: string;
  quantity: number;
  totalNeto: number;
  unitId: number;
  unitName: string;
  providerId?: number;
  providerName?: string;
  brandId?: number;
  purchaseDate: string;
  taxType: string | null;
  ivaIncluded: boolean;
  totalWithTax?: number;
  categoryId?: number;
  categoryName?: string;
  notes: string | null;
  updateStock: boolean;
  storageUnitId?: number;
}

const getDefaultDate = (): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const CreatePurchase: React.FC<CreatePurchaseProps> = ({
  show,
  setShow,
  onCreated,
  categories,
}) => {
  // Mode state
  const [mode, setMode] = useState<PurchaseMode>('existing');

  // Data lists
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);

  // Existing Product Mode - Ingredient dropdown
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showIngredientMenu, setShowIngredientMenu] = useState(false);
  const ingredientInputRef = useRef<HTMLInputElement>(null);

  // Provider dropdown (both modes)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [providerSearch, setProviderSearch] = useState("");
  const [showProviderMenu, setShowProviderMenu] = useState(false);
  const providerInputRef = useRef<HTMLInputElement>(null);

  // Brand dropdown (existing mode only)
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [brandSearch, setBrandSearch] = useState("");
  const [showBrandMenu, setShowBrandMenu] = useState(false);
  const brandInputRef = useRef<HTMLInputElement>(null);

  // Quick Purchase Mode - Ingredient name (text input)
  const [ingredientName, setIngredientName] = useState("");

  // Unit of Measure dropdown (shared between modes)
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [showUnitMenu, setShowUnitMenu] = useState(false);

  // Common fields
  const [quantity, setQuantity] = useState<string>("");
  const [totalNeto, setTotalNeto] = useState<string>("");
  const [updateStock, setUpdateStock] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // New fields
  const [purchaseDate, setPurchaseDate] = useState<string>(getDefaultDate());
  const [taxType, setTaxType] = useState<TaxType | null>(null);
  const [ivaIncluded, setIvaIncluded] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategoryType | null>(null);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [notes, setNotes] = useState<string>("");

  // Stock info for selected ingredient
  const [ingredientStocks, setIngredientStocks] = useState<IngredientStock[]>([]);
  const [isLoadingStock, setIsLoadingStock] = useState<boolean>(false);
  const [stockUnitLocked, setStockUnitLocked] = useState<boolean>(false);
  const [selectedStorageUnit, setSelectedStorageUnit] = useState<IngredientStock | null>(null);
  const quantityInputRef = useRef<HTMLInputElement | null>(null);

  // Loading state for dropdown data
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);

  // Batch / multi-item state
  const [batchMode, setBatchMode] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [pendingKeyCounter, setPendingKeyCounter] = useState(0);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      const user = JSON.parse(localStorage.getItem("user") ?? "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      
      setIsLoadingDropdowns(true);
      Promise.all([
        fetchIngredients(localId),
        fetchProviders(localId),
        fetchUnitsWithDefault(),
        fetchBrands(localId),
      ]).finally(() => setIsLoadingDropdowns(false));
      
      // Reset form
      setMode('existing');
      setSelectedIngredient(null);
      setIngredientSearch("");
      setSelectedProvider(null);
      setProviderSearch("");
      setSelectedBrand(null);
      setBrandSearch("");
      setIngredientName("");
      setQuantity("");
      setTotalNeto("");
      setUpdateStock(true);
      setIngredientStocks([]);
      setStockUnitLocked(false);
      setSelectedStorageUnit(null);
      setPurchaseDate(getDefaultDate());
      setTaxType(null);
      setIvaIncluded(false);
      setSelectedCategory(null);
      setCategorySearch("");
      setNotes("");
      setPendingItems([]);
      setPendingKeyCounter(0);
      setBatchMode(false);
    }
  }, [show]);

  const fetchIngredients = async (local_id: number) => {
    try {
      const response = await getAllIngredientsByLocalId(local_id);
      setIngredients(response.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
      toast.error("Error al cargar productos");
    }
  };

  const fetchProviders = async (local_id: number) => {
    try {
      const response = await getAllProvidersByLocalId(local_id);
      setProviders(response.data);
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Error al cargar proveedores");
    }
  };

  const fetchBrands = async (local_id: number) => {
    try {
      const response: any = await getAllBrandsByLocalId(local_id);
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  // Nueva función para setear la unidad por defecto
  const fetchUnitsWithDefault = async () => {
    try {
      const response = await getAllUnidadesMedida();
      setUnits(response.data);
      // Buscar la unidad con name 'Unidad' y setearla por defecto
      const defaultUnit = response.data.find((u: Unit) => u.name === "Unidad");
      setSelectedUnit(defaultUnit || null);
    } catch (error) {
      console.error("Error fetching units:", error);
      toast.error("Error al cargar unidades de medida");
    }
  };


  const filteredIngredients = ingredients.filter((ing) =>
    ing.ingredient_name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  const filteredProviders = providers.filter((prov) =>
    prov.name.toLowerCase().includes(providerSearch.toLowerCase())
  );

  const filteredBrands = brands.filter((brand) =>
    brand.brand_name.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const filteredCategories = categories.filter((cat) =>
    cat.category_name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Fetch ingredient stock when ingredient is selected
  useEffect(() => {
    const fetchIngredientStock = async () => {
      if (!selectedIngredient) {
        setIngredientStocks([]);
        setStockUnitLocked(false);
        return;
      }

      setIsLoadingStock(true);
      try {
        const response = await getIngredientById(selectedIngredient.ingredient_id);
        const data = response.data;
        if (data.stocks && data.stocks.length > 0) {
          setIngredientStocks(data.stocks);
          // Auto-select the first storage unit
          setSelectedStorageUnit(data.stocks[0]);
          // Lock the unit to the stock's unit
          const stockUnit = units.find(u => u.unidad_medida_id === data.stocks[0].unidad_medida_id);
          if (stockUnit) {
            setSelectedUnit(stockUnit);
            setStockUnitLocked(true);
          }
        } else {
          setIngredientStocks([]);
          setStockUnitLocked(false);
          setSelectedStorageUnit(null);
        }
      } catch (error) {
        console.error("Error fetching ingredient stock:", error);
        setIngredientStocks([]);
        setStockUnitLocked(false);
        setSelectedStorageUnit(null);
      } finally {
        setIsLoadingStock(false);
      }
    };

    fetchIngredientStock();
  }, [selectedIngredient, units]);

  // Helper to get total current stock across all storage units
  const getTotalCurrentStock = (): number => {
    return ingredientStocks.reduce((sum, stock) => sum + stock.reported_stock, 0);
  };

  // Helper to get selected storage unit's current stock
  const getSelectedStorageStock = (): number => {
    return selectedStorageUnit?.reported_stock ?? 0;
  };

  // Helper to get unit name
  const getUnitName = (): string => {
    if (selectedUnit) {
      return selectedUnit.name;
    }
    return '';
  };

  /* ── Validate current form fields ── */
  const validateCurrentItem = (): boolean => {
    if (!selectedUnit || !quantity || !totalNeto) {
      toast.error("Por favor, complete todos los campos obligatorios.");
      return false;
    }
    if (mode === 'existing') {
      if (!selectedIngredient) {
        toast.error("Por favor, seleccione un producto.");
        return false;
      }
    } else if (mode === 'quick') {
      if (!ingredientName.trim()) {
        toast.error("Por favor, ingrese el nombre del producto.");
        return false;
      }
    }
    return true;
  };

  /* ── Build a PendingItem from current form state ── */
  const buildPendingItem = (): PendingItem => {
    const key = pendingKeyCounter;
    setPendingKeyCounter((k) => k + 1);
    return {
      _key: key,
      mode: mode as 'existing' | 'quick',
      displayName:
        mode === 'existing'
          ? selectedIngredient!.ingredient_name
          : ingredientName.trim(),
      ingredientId: mode === 'existing' ? selectedIngredient!.ingredient_id : undefined,
      ingredientName: mode === 'quick' ? ingredientName.trim() : undefined,
      quantity: parseFloat(quantity),
      totalNeto: (taxType === 'deducible_iva' && ivaIncluded) ? 0 : parseFloat(totalNeto),
      unitId: selectedUnit!.unidad_medida_id,
      unitName: selectedUnit!.name,
      providerId: selectedProvider?.provider_id,
      providerName: selectedProvider?.name,
      brandId: selectedBrand?.brand_id,
      purchaseDate,
      taxType: taxType || null,
      ivaIncluded,
      totalWithTax: (taxType === 'deducible_iva' && ivaIncluded) ? parseFloat(totalNeto) : undefined,
      categoryId: selectedCategory?.expense_category_id,
      categoryName: selectedCategory?.category_name,
      notes: notes.trim() || null,
      updateStock,
      storageUnitId: updateStock && selectedStorageUnit ? selectedStorageUnit.storage_unit_id : undefined,
    };
  };

  /* ── Reset per-item fields (keep provider, date, tax for convenience) ── */
  const resetItemFields = () => {
    setSelectedIngredient(null);
    setIngredientSearch("");
    setIngredientName("");
    setQuantity("");
    setTotalNeto("");
    setSelectedBrand(null);
    setBrandSearch("");
    setSelectedCategory(null);
    setCategorySearch("");
    setNotes("");
    setIngredientStocks([]);
    setStockUnitLocked(false);
    setSelectedStorageUnit(null);
    setSelectedUnit(units.find((u) => u.name === 'Kilogramos') || null);
  };

  /* ── Add current form item to the pending list ── */
  const handleAddToList = () => {
    if (!validateCurrentItem()) return;
    const item = buildPendingItem();
    setPendingItems((prev) => [...prev, item]);
    resetItemFields();
    toast.success(`"${item.displayName}" agregado al lote`);
  };

  /* ── Remove item from pending list ── */
  const handleRemovePending = (key: number) => {
    setPendingItems((prev) => prev.filter((i) => i._key !== key));
  };

  /* ── Save: batch if pending items, single otherwise ── */
  const handleSave = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const localId = user.local_id ? Number(user.local_id) : 0;
    const userId = user.user_id ? Number(user.user_id) : null;

    // If batch mode with pending items → batch save
    if (batchMode && pendingItems.length > 0) {
      setIsLoading(true);
      try {
        const payloads: Omit<CreatePurchasePayload, 'local_id' | 'user_id'>[] = pendingItems.map((p) => ({
          invoice_id: null,
          ingredient_id: p.ingredientId || null,
          ingredient_name: p.ingredientName || null,
          quantity: p.quantity,
          total_neto: p.totalNeto,
          unidad_medida_id: p.unitId,
          provider_id: p.providerId || null,
          brand_id: p.brandId || null,
          update_stock: p.updateStock,
          storage_unit_id: p.storageUnitId || null,
          purchase_date: p.purchaseDate,
          tax_type: p.taxType,
          total_with_tax: p.totalWithTax,
          expense_category_id: p.categoryId || null,
          notes: p.notes,
        }));
        await createBatchIngredientPurchaseRecords(localId, userId, payloads);
        toast.success(`${pendingItems.length} compra(s) creadas exitosamente`);
        onCreated();
        setShow(false);
      } catch (error: any) {
        console.error("Error batch creating purchases:", error);
        toast.error(error?.response?.data?.error || error?.response?.data?.message || "Error al crear las compras");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // No pending items → single create (current behavior)
    if (!validateCurrentItem()) return;

    const payload: CreatePurchasePayload = {
      local_id: localId,
      invoice_id: null,
      quantity: parseFloat(quantity),
      total_neto: (taxType === 'deducible_iva' && ivaIncluded) ? 0 : parseFloat(totalNeto),
      unidad_medida_id: selectedUnit!.unidad_medida_id,
      update_stock: updateStock,
      user_id: userId,
      purchase_date: purchaseDate,
      tax_type: taxType || null,
      total_with_tax: (taxType === 'deducible_iva' && ivaIncluded) ? parseFloat(totalNeto) : undefined,
      expense_category_id: selectedCategory?.expense_category_id || null,
      notes: notes.trim() || null,
    };

    if (mode === 'existing') {
      payload.ingredient_id = selectedIngredient!.ingredient_id;
      if (selectedProvider) payload.provider_id = selectedProvider.provider_id;
      if (selectedBrand) payload.brand_id = selectedBrand.brand_id;
      if (updateStock && selectedStorageUnit) payload.storage_unit_id = selectedStorageUnit.storage_unit_id;
    } else {
      payload.ingredient_name = ingredientName.trim();
      if (selectedProvider) payload.provider_id = selectedProvider.provider_id;
    }

    setIsLoading(true);
    try {
      await createIngredientPurchaseRecord(payload);
      toast.success("Compra creada exitosamente");
      onCreated();
      setShow(false);
    } catch (error: any) {
      console.error("Error creating ingredient purchase:", error);
      toast.error(error?.response?.data?.message || "Error al crear la compra");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} centered className="create-purchase-modal" container={document.getElementById('root') || undefined}>
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Crear Compra</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={() => setShow(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        <Form>
          {/* 1. Mode Selector */}
          <div className="flex gap-2 mb-4 p-1.5 rounded-mp-md border border-mp-border">
            <div
              className={`flex-1 py-2 px-3 rounded-md text-center font-medium text-mp-sm cursor-pointer transition-all duration-200 ${
                mode === 'existing'
                  ? 'bg-mp-orange text-white shadow-mp-orange'
                  : 'text-mp-text hover:bg-mp-orange-ultra-light'
              }`}
              onClick={() => setMode('existing')}
            >
              <HiOutlineCube className="inline -mt-0.5 mr-1" />
              Producto Existente
            </div>
            <div
              className={`flex-1 py-2 px-3 rounded-md text-center font-medium text-mp-sm cursor-pointer transition-all duration-200 ${
                mode === 'quick'
                  ? 'bg-mp-orange text-white shadow-mp-orange'
                  : 'text-mp-text hover:bg-mp-orange-ultra-light'
              }`}
              onClick={() => setMode('quick')}
            >
              <HiOutlineBolt className="inline -mt-0.5 mr-1" />
              Compra Rápida
            </div>
          </div>

          {/* Batch mode toggle */}
          {(
            <div
              className={`flex items-center gap-2.5 mb-4 px-3 py-2.5 rounded-mp-md border cursor-pointer transition-all duration-200 select-none ${
                batchMode
                  ? 'border-mp-orange/40 bg-mp-orange/5'
                  : 'border-mp-border hover:border-mp-orange/20'
              }`}
              onClick={() => setBatchMode(!batchMode)}
            >
              <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${batchMode ? 'bg-mp-orange' : 'bg-mp-border'}`}>
                <div className={`absolute top-[2px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${batchMode ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
              </div>
              <HiOutlineQueueList className={`text-lg shrink-0 ${batchMode ? 'text-mp-orange' : 'text-mp-text-muted'}`} />
              <div className="flex-1">
                <span className={`text-mp-sm font-medium ${batchMode ? 'text-mp-orange' : 'text-mp-text'}`}>
                  Múltiples Productos
                </span>
                <span className="text-mp-xs text-mp-text-muted ml-2">
                  Agrega varios ítems en una sola operación
                </span>
              </div>
              {pendingItems.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-mp-orange text-white text-mp-xs font-bold px-1.5">
                  {pendingItems.length}
                </span>
              )}
            </div>
          )}
          {mode === 'quick' && (
            <div className="flex items-start gap-2.5 p-3 mb-3 rounded-mp-md bg-blue-50/60 border border-blue-200/40">
              <FaInfoCircle className="text-blue-500 mt-0.5 shrink-0" />
              <p className="text-mp-xs text-mp-text leading-relaxed m-0">
                <span className="font-semibold text-blue-600">Compra Rápida:</span> Ideal para productos no registrados. Solo necesitas ingresar el nombre del producto.
              </p>
            </div>
          )}

          {/* Document Mode removed: AI expense-extraction backend decommissioned */}

          {/* ─── Form fields (existing / quick modes) ─── */}
          {(
          <>
          {/* 3. Product Fields */}
          {mode === 'existing' ? (
            <div className="mb-3">
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">
                Producto <span className="text-mp-orange">*</span>
              </label>
              <Dropdown
                show={showIngredientMenu}
                onToggle={(isOpen) => {
                  setShowIngredientMenu(isOpen);
                  if (isOpen && ingredientInputRef.current) {
                    setTimeout(() => ingredientInputRef.current?.focus(), 100);
                  }
                }}
              >
                <Dropdown.Toggle variant="none" className="w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200">
                  {selectedIngredient ? selectedIngredient.ingredient_name : <span className="text-mp-text-muted/50">Seleccione un producto...</span>}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <div className="dropdown-search">
                    <input
                      ref={ingredientInputRef}
                      type="text"
                      placeholder="Buscar producto..."
                      value={ingredientSearch}
                      onChange={(e) => setIngredientSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {isLoadingDropdowns ? (
                    <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando productos...</Dropdown.Item>
                  ) : filteredIngredients.length === 0 ? (
                    <Dropdown.Item disabled>No se encontraron productos</Dropdown.Item>
                  ) : (
                    filteredIngredients.map((ing) => (
                      <Dropdown.Item
                        key={ing.ingredient_id}
                        onClick={() => {
                          setSelectedIngredient(ing);
                          setIngredientSearch("");
                          setShowIngredientMenu(false);
                        }}
                      >
                        {ing.ingredient_name}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          ) : (
            <div className="mb-3">
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">
                Nombre del Producto <span className="text-mp-orange">*</span>
              </label>
              <Form.Control
                type="text"
                className="w-full px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                placeholder="Ej: Tomate Cherry"
                value={ingredientName}
                onChange={(e) => setIngredientName(e.target.value)}
              />
            </div>
          )}

          {/* 4. Provider + Brand Row (existing) / Provider only (quick) */}
          {mode === 'existing' ? (
            <div className="grid grid-cols-1 mp-sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Proveedor</label>
                <Dropdown
                  show={showProviderMenu}
                  onToggle={(isOpen) => {
                    setShowProviderMenu(isOpen);
                    if (isOpen && providerInputRef.current) {
                      setTimeout(() => providerInputRef.current?.focus(), 100);
                    }
                  }}
                >
                  <Dropdown.Toggle variant="none" className="w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200">
                    {selectedProvider ? selectedProvider.name : <span className="text-mp-text-muted/50">Seleccione...</span>}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <div className="dropdown-search">
                      <input
                        ref={providerInputRef}
                        type="text"
                        placeholder="Buscar proveedor..."
                        value={providerSearch}
                        onChange={(e) => setProviderSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <Dropdown.Item onClick={() => { setSelectedProvider(null); setProviderSearch(""); setShowProviderMenu(false); }}>
                      <span className="text-mp-text-muted italic">Ninguno</span>
                    </Dropdown.Item>
                    {isLoadingDropdowns ? (
                      <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando proveedores...</Dropdown.Item>
                    ) : filteredProviders.length === 0 ? (
                      <Dropdown.Item disabled>No se encontraron proveedores</Dropdown.Item>
                    ) : (
                      filteredProviders.map((prov) => (
                        <Dropdown.Item key={prov.provider_id} onClick={() => { setSelectedProvider(prov); setProviderSearch(""); setShowProviderMenu(false); }}>
                          {prov.name}
                        </Dropdown.Item>
                      ))
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div>
                <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Marca</label>
                <Dropdown
                  show={showBrandMenu}
                  onToggle={(isOpen) => {
                    setShowBrandMenu(isOpen);
                    if (isOpen && brandInputRef.current) {
                      setTimeout(() => brandInputRef.current?.focus(), 100);
                    }
                  }}
                >
                  <Dropdown.Toggle variant="none" className="w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200">
                    {selectedBrand ? selectedBrand.brand_name : <span className="text-mp-text-muted/50">Seleccione...</span>}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <div className="dropdown-search">
                      <input
                        ref={brandInputRef}
                        type="text"
                        placeholder="Buscar marca..."
                        value={brandSearch}
                        onChange={(e) => setBrandSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <Dropdown.Item onClick={() => { setSelectedBrand(null); setBrandSearch(""); setShowBrandMenu(false); }}>
                      <span className="text-mp-text-muted italic">Ninguna</span>
                    </Dropdown.Item>
                    {isLoadingDropdowns ? (
                      <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando marcas...</Dropdown.Item>
                    ) : filteredBrands.length === 0 ? (
                      <Dropdown.Item disabled>No se encontraron marcas</Dropdown.Item>
                    ) : (
                      filteredBrands.map((brand) => (
                        <Dropdown.Item key={brand.brand_id} onClick={() => { setSelectedBrand(brand); setBrandSearch(""); setShowBrandMenu(false); }}>
                          {brand.brand_name}
                        </Dropdown.Item>
                      ))
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          ) : (
            <div className="mb-3">
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Proveedor</label>
              <Dropdown
                show={showProviderMenu}
                onToggle={(isOpen) => {
                  setShowProviderMenu(isOpen);
                  if (isOpen && providerInputRef.current) {
                    setTimeout(() => providerInputRef.current?.focus(), 100);
                  }
                }}
              >
                <Dropdown.Toggle variant="none" className="w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200">
                  {selectedProvider ? selectedProvider.name : <span className="text-mp-text-muted/50">Seleccione un proveedor...</span>}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <div className="dropdown-search">
                    <input
                      ref={providerInputRef}
                      type="text"
                      placeholder="Buscar proveedor..."
                      value={providerSearch}
                      onChange={(e) => setProviderSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <Dropdown.Item onClick={() => { setSelectedProvider(null); setProviderSearch(""); setShowProviderMenu(false); }}>
                    <span className="text-mp-text-muted italic">Ninguno</span>
                  </Dropdown.Item>
                  {isLoadingDropdowns ? (
                    <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando proveedores...</Dropdown.Item>
                  ) : filteredProviders.length === 0 ? (
                    <Dropdown.Item disabled>No se encontraron proveedores</Dropdown.Item>
                  ) : (
                    filteredProviders.map((prov) => (
                      <Dropdown.Item key={prov.provider_id} onClick={() => { setSelectedProvider(prov); setProviderSearch(""); setShowProviderMenu(false); }}>
                        {prov.name}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}

          {/* 6. Stock Information Section (existing mode only) */}
          {mode === 'existing' && selectedIngredient && (
            <div className="stock-info-section">
              <div className="stock-info-header">
                <HiOutlineArchiveBox className="stock-icon" />
                <span>Información de Stock</span>
              </div>
              
              {isLoadingStock ? (
                <div className="stock-loading">
                  <Spinner animation="border" size="sm" />
                  <span>Cargando stock...</span>
                </div>
              ) : ingredientStocks.length > 0 ? (
                <div className="stock-info-content">
                  <div className="stock-summary">
                    <div className="stock-summary-item">
                      <span className="stock-label">Stock Actual Total:</span>
                      <span className="stock-value">{getTotalCurrentStock()} {getUnitName()}</span>
                    </div>
                  </div>

                  {ingredientStocks.length > 1 && updateStock && (
                    <div className="storage-unit-selector">
                      <span className="selector-label">Almacén destino:</span>
                      <div className="storage-options">
                        {ingredientStocks.map((stock, index) => (
                          <div 
                            key={index} 
                            className={`storage-option ${selectedStorageUnit?.storage_unit_id === stock.storage_unit_id ? 'selected' : ''}`}
                            onClick={() => setSelectedStorageUnit(stock)}
                          >
                            <span className="storage-name">{stock.storage_unit.name}</span>
                            <span className={`storage-type-badge ${stock.storage_unit.is_global ? 'global' : 'local'}`}>
                              {stock.storage_unit.is_global ? 'Global' : 'Local'}
                            </span>
                            <span className="storage-stock">{stock.reported_stock} {getUnitName()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(ingredientStocks.length === 1 || !updateStock) && (
                    <div className="stock-breakdown">
                      {ingredientStocks.map((stock, index) => (
                        <div key={index} className="stock-breakdown-item">
                          <span className="storage-name">{stock.storage_unit.name}</span>
                          <span className={`storage-type-badge ${stock.storage_unit.is_global ? 'global' : 'local'}`}>
                            {stock.storage_unit.is_global ? 'Global' : 'Local'}
                          </span>
                          <span className="storage-stock">{stock.reported_stock} {getUnitName()}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {quantity && parseFloat(quantity) > 0 && updateStock && selectedStorageUnit && (
                    <div className="stock-projection">
                      <div className="projection-label">
                        Proyección en <strong>{selectedStorageUnit.storage_unit.name}</strong>:
                      </div>
                      <div className="projection-visual">
                        <span className="current-stock">{getSelectedStorageStock()}</span>
                        <HiOutlineArrowRight className="arrow-icon" />
                        <span className="projected-stock">{getSelectedStorageStock() + parseFloat(quantity)}</span>
                        <span className="unit-label">{getUnitName()}</span>
                      </div>
                      <div className="projection-increase">
                        <span className="increase-badge">+{parseFloat(quantity)} {getUnitName()}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-stock-info">
                  <FaInfoCircle className="info-icon" />
                  <span>Este producto no tiene stock configurado</span>
                </div>
              )}
            </div>
          )}

          {/* 7. Quantity + Unit */}
          <div className="flex gap-3 mb-3">
            <div className="flex-[1.5]">
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">
                Cantidad <span className="text-mp-orange">*</span>
              </label>
              <Form.Control
                type="number"
                step="0.1"
                min="0"
                className="w-full px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                ref={(el: HTMLInputElement | null) => (quantityInputRef.current = el)}
                placeholder="Cantidad"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || parseFloat(val) >= 0) setQuantity(val);
                }}
                onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
              />
            </div>

            <div className="flex-1">
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">
                Unidad <span className="text-mp-orange">*</span>
              </label>
              <Dropdown
                show={!stockUnitLocked && showUnitMenu}
                onToggle={(isOpen) => !stockUnitLocked && setShowUnitMenu(isOpen)}
              >
                <Dropdown.Toggle 
                  variant="none" 
                  className={`w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200 ${stockUnitLocked ? 'locked' : ''}`}
                  disabled={stockUnitLocked}
                >
                  {selectedUnit ? selectedUnit.name : "Seleccione..."}
                  {stockUnitLocked && <span className="ml-1.5 text-[10px]">🔒</span>}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {isLoadingDropdowns ? (
                    <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando unidades...</Dropdown.Item>
                  ) : units.length === 0 ? (
                    <Dropdown.Item disabled>No hay unidades</Dropdown.Item>
                  ) : (
                    units.map((unit) => (
                      <Dropdown.Item key={unit.unidad_medida_id} onClick={() => { setSelectedUnit(unit); setShowUnitMenu(false); }}>
                        {unit.name}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* 8. Total Neto with $ prefix */}
          <div className="mb-3">
            <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">
              {taxType === 'deducible_iva' && ivaIncluded ? 'Total con IVA' : 'Total Neto'} <span className="text-mp-orange">*</span>
            </label>
            <div className="flex items-center border border-mp-border rounded-mp-md overflow-hidden transition-all duration-200 focus-within:border-mp-orange focus-within:ring-2 focus-within:ring-mp-orange/10">
              <span className="flex items-center justify-center px-3 text-mp-base font-semibold text-mp-text-muted bg-mp-bg/30 border-r border-mp-border h-[42px] select-none">$</span>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                className="flex-1 px-3 py-2.5 bg-transparent border-0 text-mp-base text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:ring-0 focus:shadow-none"
                placeholder={taxType === 'deducible_iva' && ivaIncluded ? 'Total con IVA incluido' : 'Ingrese el total neto'}
                value={totalNeto}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || parseFloat(val) >= 0) setTotalNeto(val);
                }}
                onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
              />
            </div>
          </div>

          {/* 9. Tax Type Selector */}
          <div className="mb-3">
            <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Tipo de Comprobante</label>
            <div className="flex gap-2 mp-sm:flex-row flex-col">
              {TAX_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className={`flex-1 py-2.5 px-2 rounded-mp-md border-2 cursor-pointer transition-all duration-200 text-center ${
                    taxType === option.value
                      ? 'border-mp-orange bg-mp-orange-ultra-light shadow-sm'
                      : 'border-mp-border bg-transparent hover:border-mp-orange/30 hover:bg-mp-orange-ultra-light/50'
                  }`}
                  onClick={() => {
                    setTaxType(taxType === option.value ? null : option.value);
                    if (option.value !== 'deducible_iva') setIvaIncluded(false);
                  }}
                >
                  <span className={`block font-semibold text-mp-xs ${taxType === option.value ? 'text-mp-orange' : 'text-mp-text'}`}>
                    {option.label}
                  </span>
                  <span className="block text-[10px] text-mp-text-muted leading-tight mt-0.5">{option.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 10. IVA Toggle */}
          {taxType === 'deducible_iva' && (
            <div className="mb-3">
              <div className="flex rounded-mp-md overflow-hidden border border-mp-border">
                <div
                  className={`flex-1 py-2 px-3 text-center text-mp-sm font-medium cursor-pointer transition-all duration-200 ${
                    !ivaIncluded ? 'bg-mp-orange text-white' : 'bg-transparent text-mp-text hover:bg-mp-orange-ultra-light'
                  }`}
                  onClick={() => setIvaIncluded(false)}
                >
                  Valor sin IVA
                </div>
                <div
                  className={`flex-1 py-2 px-3 text-center text-mp-sm font-medium cursor-pointer transition-all duration-200 border-l border-mp-border ${
                    ivaIncluded ? 'bg-mp-orange text-white' : 'bg-transparent text-mp-text hover:bg-mp-orange-ultra-light'
                  }`}
                  onClick={() => setIvaIncluded(true)}
                >
                  Valor incluye IVA
                </div>
              </div>
              <p className="mt-1.5 text-mp-xs text-mp-text-muted italic m-0">
                {ivaIncluded
                  ? 'El monto ingresado incluye IVA. El sistema calculará el neto automáticamente.'
                  : 'El monto ingresado es el valor neto. El sistema calculará el IVA por separado.'}
              </p>
            </div>
          )}

          {/* 11. Category + Date Row */}
          <div className="grid grid-cols-1 mp-sm:grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Categoría</label>
              <Dropdown
                show={showCategoryMenu}
                onToggle={(isOpen) => setShowCategoryMenu(isOpen)}
              >
                <Dropdown.Toggle variant="none" className="w-full text-left flex justify-between items-center px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text hover:border-mp-orange/30 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200">
                  {selectedCategory ? selectedCategory.category_name : <span className="text-mp-text-muted/50">Seleccione...</span>}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <div className="dropdown-search">
                    <input
                      type="text"
                      placeholder="Buscar categoría..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <Dropdown.Item onClick={() => { setSelectedCategory(null); setCategorySearch(""); setShowCategoryMenu(false); }}>
                    <span className="text-mp-text-muted italic">Ninguna</span>
                  </Dropdown.Item>
                  {isLoadingDropdowns ? (
                    <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando categorías...</Dropdown.Item>
                  ) : filteredCategories.length === 0 ? (
                    <Dropdown.Item disabled>No se encontraron categorías</Dropdown.Item>
                  ) : (
                    filteredCategories.map((cat) => (
                      <Dropdown.Item key={cat.expense_category_id} onClick={() => { setSelectedCategory(cat); setCategorySearch(""); setShowCategoryMenu(false); }}>
                        {cat.category_name}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div>
              <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Fecha de Compra</label>
              <input
                type="date"
                className="w-full px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>
          </div>

          {/* 12. Notes */}
          <div className="mb-3">
            <label className="block text-mp-sm font-semibold text-mp-text mb-1.5">Notas</label>
            <textarea
              rows={2}
              className="w-full px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200 resize-y min-h-[48px] max-h-[120px]"
              placeholder="Notas opcionales sobre esta compra..."
              value={notes}
              maxLength={512}
              onChange={(e) => setNotes(e.target.value)}
            />
            {notes.length > 0 && (
              <p className="text-right text-mp-xs text-mp-text-muted mt-1 m-0">{notes.length}/512</p>
            )}
          </div>

          {/* 13. Update Stock Checkbox */}
          {mode === 'existing' && (
            <div
              className="flex items-center gap-3 p-3 rounded-mp-md border border-mp-border cursor-pointer transition-all duration-200 hover:border-mp-orange/30 mt-3"
              onClick={() => setUpdateStock(!updateStock)}
            >
              <div className={`relative w-10 h-[22px] rounded-full transition-colors duration-200 shrink-0 ${updateStock ? 'bg-mp-orange' : 'bg-mp-border'}`}>
                <div className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform duration-200 ${updateStock ? 'translate-x-[20px]' : 'translate-x-[2px]'}`} />
              </div>
              <span className="text-mp-sm font-medium text-mp-text select-none">Actualizar Stock</span>
              <span className="ml-auto text-mp-xs text-mp-text-muted italic">
                {updateStock ? "El inventario se actualizará" : "Solo se registrará la compra"}
              </span>
            </div>
          )}

          {/* ── Pending items list (batch mode only) ── */}
          {batchMode && (
            <div className="mt-4 rounded-xl border border-mp-border overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/60 border-b border-mp-border">
                <span className="text-mp-xs font-semibold text-mp-text-muted uppercase tracking-wide">
                  Productos en el lote {pendingItems.length > 0 && `(${pendingItems.length})`}
                </span>
                {pendingItems.length > 0 && (
                  <span className="text-mp-sm font-bold text-mp-text">
                    ${pendingItems.reduce((s, i) => s + (i.totalWithTax ?? i.totalNeto), 0).toLocaleString('es-CL', { minimumFractionDigits: 0 })}
                  </span>
                )}
              </div>
              {pendingItems.length === 0 ? (
                <div className="flex flex-col items-center py-6 px-4 text-center">
                  <HiOutlineQueueList className="text-3xl text-mp-text-muted/40 mb-2" />
                  <p className="text-mp-xs text-mp-text-muted m-0">
                    Completa los datos del producto y presiona <strong>"Agregar"</strong> para añadirlo al lote.
                  </p>
                  <p className="text-mp-xs text-mp-text-muted/60 m-0 mt-1">
                    Repite para cada producto. Luego guarda todo de una vez.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-mp-border/50 max-h-48 overflow-y-auto">
                  {pendingItems.map((item) => (
                    <div key={item._key} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50/40">
                      <div className="flex-1 min-w-0">
                        <p className="text-mp-sm font-medium text-mp-text truncate m-0">{item.displayName}</p>
                        <p className="text-mp-xs text-mp-text-muted m-0">
                          {item.quantity} {item.unitName} &middot; ${(item.totalWithTax ?? item.totalNeto).toLocaleString('es-CL')}
                          {item.providerName ? ` · ${item.providerName}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="mp-btn-icon-danger shrink-0"
                        onClick={() => handleRemovePending(item._key)}
                        title="Quitar del lote"
                      >
                        <HiOutlineTrash className="text-base" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}

        </Form>
      </Modal.Body>

      {(
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={() => setShow(false)} disabled={isLoading}>
            Cancelar
          </Button>
          {batchMode && (
            <Button
              variant="none"
              className="form-button-cancel"
              onClick={handleAddToList}
              disabled={isLoading}
              style={{ borderColor: 'var(--mp-orange)', color: 'var(--mp-orange)' }}
            >
              <HiOutlinePlus className="inline -mt-0.5 mr-1" />
              Agregar
            </Button>
          )}
          <Button variant="none" className="form-button-create" onClick={handleSave} disabled={isLoading || (batchMode && pendingItems.length === 0)}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Guardando...
              </>
            ) : batchMode ? (
              pendingItems.length > 0
                ? `Guardar ${pendingItems.length} compra${pendingItems.length !== 1 ? 's' : ''}`
                : "Guardar compras"
            ) : (
              "Crear compra"
            )}
          </Button>
        </div>
      </Modal.Footer>
      )}
    </Modal>
  );
};

export default CreatePurchase;
